import {useMemo} from 'react';

import {useLanesForDocument} from 'app/lanes/api';

import type {AssignedDocType} from '../PickDocumentForm';
import type {AssignableLaneWithStepType} from './assignables-lists/AssignableLanes';
import type {AssignableStepType} from './assignables-lists/AssignableSteps';
import type {AnyStepType, LaneSerializedType} from 'sweet-types';

export function useDocumentStepsAndLanes(document: AssignedDocType): {
  steps: AssignableStepType[];
  lanes: AssignableLaneWithStepType[];
} {
  const documentLanes =
    useLanesForDocument({
      object_ids: document.id,
      content_type: document.content_type,
    }).currentData?.results ?? [];

  const lanesMap = useMemo(() => {
    return documentLanes.reduce((map, lane) => {
      map[lane.hashid] = lane;
      return map;
    }, {} as {[id: string]: LaneSerializedType});
  }, [documentLanes]);

  const {steps, lanes} = useMemo(
    () => extractDocumentStepsAndLanes(document, lanesMap),
    [document, lanesMap]
  );

  return {steps, lanes};
}

/*
  here's where all the magic happens 🦄 - the document type is identified,
  the assignable steps and lanes are extracted into an easy-to-use format
*/
function extractDocumentStepsAndLanes(
  doc: AssignedDocType,
  lanesMap: {[laneid: string]: LaneSerializedType}
): {
  steps: Array<AssignableStepType>;
  lanes: Array<AssignableLaneWithStepType>;
} {
  const steps: Array<AssignableStepType> = (
    doc.content as Array<AnyStepType>
  ).filter(
    (step: AnyStepType): step is AssignableStepType =>
      step.type === null ||
      step.type === 'content' ||
      step.type === 'decision' ||
      (step.type === 'processprocedure' && !step.deleted)
  );

  const lanesWithSteps: Array<{
    lane: LaneSerializedType;
    stepsInLane: Array<AssignableStepType>;
  }> = [];

  stepsFor: for (const step of steps) {
    const stepLaneId = doc.lanes[step.id];

    if (stepLaneId != null && lanesMap[stepLaneId] != undefined) {
      for (const {lane, stepsInLane} of lanesWithSteps) {
        if (lane.hashid === stepLaneId) {
          stepsInLane.push(step);
          continue stepsFor;
        }
      }

      lanesWithSteps.push({
        lane: lanesMap[stepLaneId],
        stepsInLane: [step],
      });
    }
  }

  return {steps, lanes: lanesWithSteps};
}
